import { useInfiniteDeactivatedUsers, useUserActivation } from '@/hooks/user/users';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import UsersTable from '../UsersTable';
import { TUserActivationToggle, TUserFilterOption } from '@/types/User.types';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import { MIN_CHARS_TO_DO_SEARCH } from '@/constants/numberVars';
import DeactivatedUsersActionsPopover from './DeactivatedUsersActionsPopover';
import ReassignUserOrganizationModal from './ReassignUserOrganizationModal';

interface Props {
  filterOption?: TUserFilterOption;
  filterText: string;
  setIsLoadingSearch: Dispatch<SetStateAction<boolean>>;
}

const DeactivatedUsersTable = ({ filterOption, filterText, setIsLoadingSearch }: Props) => {
  const { isLoading, data, refetch, isRefetching, infinitePagination } = useInfiniteDeactivatedUsers({
    filterOption,
    filterText,
  });

  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isReassignModalOpened, setIsReassignModalOpened] = useState(false);

  const [activatingUser, setActivatingUser] = useState<TUserActivationToggle>();

  const { mutate, isPending } = useUserActivation();

  const isSearching = useMemo(
    () => filterOption !== undefined && filterText.length >= MIN_CHARS_TO_DO_SEARCH && isRefetching,
    [filterOption, filterText, isRefetching],
  );

  useEffect(() => {
    setIsLoadingSearch(isSearching);
  }, [isSearching]);

  const onActivationSuccess = () => {
    setIsModalOpened(false);
    refetch();
  };

  const activateSelectedUser = () => {
    if (activatingUser) {
      mutate({ userId: activatingUser.id }, { onSuccess: onActivationSuccess });
    }
  };

  return (
    <>
      <UsersTable
        users={data}
        infinitePagination={infinitePagination}
        isLoading={isLoading}
        isRefetching={isRefetching}
        isSearching={isSearching}
        invalidatedUserId={activatingUser?.id}
        ActionElement={(userToActivate: TUserActivationToggle) => (
          <DeactivatedUsersActionsPopover
            user={userToActivate}
            onActivate={(user) => {
              setActivatingUser(user);
              setIsModalOpened(true);
            }}
            onReassign={(user) => {
              setActivatingUser(user);
              setIsReassignModalOpened(true);
            }}
          />
        )}
      />
      <ConfirmationModal
        isOpen={isModalOpened}
        onCancel={() => {
          setIsModalOpened(false);
          setActivatingUser(undefined);
        }}
        title="Confirm Activation"
        confirmButtonText="Activate"
        description={`This will activate user ${activatingUser?.firstName} ${activatingUser?.lastName}`}
        onConfirm={activateSelectedUser}
        confirmButtonColor="primary.500"
        confirmLoading={isPending}
      />
      <ReassignUserOrganizationModal
        isOpen={isReassignModalOpened}
        onClose={() => {
          setIsReassignModalOpened(false);
          setActivatingUser(undefined);
        }}
        user={activatingUser}
      />
    </>
  );
};

export default DeactivatedUsersTable;
